import { useState } from 'react'

// One page for every curated Grasshopper script (see ../../ghScripts.js). These are
// fixed, tested components, so there is nothing to generate and no backend call: the
// Python is bundled from the golden via ?raw, and the .gh is a static asset.
export default function GhScriptPage({ script }) {
  const [showHelp, setShowHelp] = useState(true)
  const [copied, setCopied] = useState(false)

  // A route pointing at an id that is not in the registry would otherwise blank the
  // whole page with a TypeError. Say which id is missing instead.
  if (!script) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 text-sm text-gray-700">
        This script is not in the registry — check that the id used in{' '}
        <code className="bg-gray-50 px-1 rounded border">App.jsx</code> matches an entry
        in <code className="bg-gray-50 px-1 rounded border">src/ghScripts.js</code>.
      </div>
    )
  }

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(script.code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard blocked — user can select manually */ }
  }

  function downloadPy() {
    const blob = new Blob([script.code], { type: 'text/x-python' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = script.pyName
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">{script.title}</h2>
        <p className="text-sm text-gray-500 mt-0.5">{script.blurb}</p>
      </div>

      {/* Params */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid gap-6 sm:grid-cols-2">
          {[['Inputs', script.inputs], ['Outputs', script.outputs]].map(([heading, rows]) => (
            <div key={heading}>
              <div className="text-sm font-semibold text-gray-800 mb-2">{heading}</div>
              <dl className="space-y-1.5">
                {rows.map(([name, type, desc]) => (
                  <div key={name} className="text-sm">
                    <dt className="inline">
                      <code className="bg-gray-50 px-1 rounded border text-gray-800">{name}</code>
                      <span className="text-xs text-gray-400 ml-1.5">{type}</span>
                    </dt>
                    <dd className="text-gray-600 text-xs mt-0.5">{desc}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
        {script.notes?.length > 0 && (
          <ul className="mt-4 pt-3 border-t border-gray-100 space-y-1.5 list-disc ml-5 text-xs text-gray-600">
            {script.notes.map((n) => <li key={n}>{n}</li>)}
          </ul>
        )}
      </div>

      {/* How to use */}
      <div className="bg-brand-50 border border-brand-100 rounded-xl">
        <button
          onClick={() => setShowHelp((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
        >
          <span className="text-sm font-semibold text-brand-900">
            How to use this in Rhino 8 / Grasshopper
          </span>
          <span className="text-brand-500 text-sm">{showHelp ? 'Hide ▲' : 'Show ▼'}</span>
        </button>
        {showHelp && (
          <div className="px-4 pb-4 space-y-4 text-sm text-gray-700">
            {/* Only worth explaining while there is a .gh to download — otherwise this
                walks the user through a file the page cannot give them. */}
            {script.ghUrl && (
              <div>
                <div className="font-semibold text-gray-800">Easiest — Download .gh</div>
                <ol className="list-decimal ml-5 mt-1 space-y-1">
                  <li>Open <span className="font-medium">Rhino 8</span>, then start Grasshopper (type <code className="bg-white px-1 rounded border">Grasshopper</code> in Rhino&apos;s command line).</li>
                  <li>In Grasshopper: <span className="font-medium">File → Open</span> → the downloaded <code className="bg-white px-1 rounded border">{script.ghName}</code>. The component arrives already wired, with every param named correctly.</li>
                  <li>{script.ghStep}</li>
                </ol>
              </div>
            )}

            <div>
              <div className="font-semibold text-gray-800">Full control — Copy code / Download .py</div>
              <ol className="list-decimal ml-5 mt-1 space-y-1">
                <li>In Grasshopper: <span className="font-medium">Maths → Script → Python 3 Script (Py3)</span>. Double-click it, paste the code, save, close.</li>
                <li><span className="font-medium">Add the params by hand.</span> A fresh Py3 component starts with <code className="bg-white px-1 rounded border">x, y → out</code>, and pasting code does <span className="font-medium">not</span> create them. Zoom in, use the small <code className="bg-white px-1 rounded border">+</code> on the input/output columns, and rename each (double-click the name letter) to match the table above.</li>
                <li>{script.pyStep}</li>
              </ol>
              <p className="mt-1.5 text-xs text-gray-500">
                The right-click <span className="font-medium">&quot;Name (for humans)&quot;</span> field is only a display
                label, <span className="font-medium">not</span> the variable name. Leave the{' '}
                <code className="bg-white px-1 rounded border">out</code> stream alone — that is the print/console
                output, and it is where {script.outNote} appears.
              </p>
            </div>

            {/* Per-script, from the registry. A hardcoded warning here previously told
                perforation users about mesh-surface picking, which that script has no
                concept of. */}
            {script.warning && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                {script.warning}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Downloads */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Rendered only once the .gh asset is committed — until then the .py path
              is fully functional, so an absent button beats a broken download. */}
          {script.ghUrl && (
            <a
              href={script.ghUrl}
              download={script.ghName}
              className="btn-primary text-sm px-4 py-2"
            >
              Download .gh
            </a>
          )}
          <button onClick={downloadPy} className="btn-secondary text-sm px-4 py-2">
            Download .py
          </button>
          <button onClick={copyCode} className="btn-secondary text-sm px-4 py-2">
            {copied ? 'Copied ✓' : 'Copy code'}
          </button>
          <span className="text-xs text-gray-400 ml-auto">
            Rhino 8 · Python 3 · RhinoCommon
          </span>
        </div>
        <textarea
          readOnly
          value={script.code}
          rows={22}
          spellCheck={false}
          className="w-full font-mono text-xs bg-gray-900 text-gray-100 rounded-lg p-3 border border-gray-700"
        />
      </div>
    </div>
  )
}
